import { buildMasteringChain, measureLufs, measureTruePeak, measureSpectralTilt, suggestRecipe, MASTERING_TARGETS } from "@/lib/mastering";
import { makeReverbIR, audioBufferToWavBlob } from "@/lib/audioEngineUtils";
import { encodeWav } from "@/lib/wavEncoder";
import { EFFECT_KEYS, FX_PARAM_DEFAULTS, FX_PARAM_SCHEMA, buildFxChain } from "@/lib/audioEngine/fxRecipes";
import { Channel, downsample } from "@/lib/audioEngine/channel";
import { scheduleSynthNote, previewMidiNote, renderMidiClipToBuffer } from "@/lib/audioEngine/synth";
import { Metronome } from "@/lib/audioEngine/scheduler";
// iter-265 — Pitch-preserving SOLA time-stretch, used by stretchChannel.
// Namespace import (not `import { timeStretch as _solaTimeStretch }`)
// because the named-binding form gets resolved to a static binding by
// Babel's CJS interop, which jest.mock() can't override. Reading
// `_tempoModule.timeStretch` at call time always picks up the current
// module export — including any jest.fn replacement.
import * as _tempoModule from "@/lib/tempoDetection";
import logger from "@/lib/logger";

// Web Audio engine powering ELPM Studio.
// Each "channel" owns: AudioBuffer (or null), gain, pan, mute/solo state, peak meter,
// and a full per-channel FX chain: AutoTune/Pitch (LFO→detune), 3-band EQ (BiquadFilter),
// Compressor, parallel Reverb (ConvolverNode) & Delay (DelayNode + feedback), Limiter.
// Effects toggle to identity (zero gain / 1:1 ratio) when "off" so the graph never rewires.
//
// iter-72 — `Channel` lives in /lib/audioEngine/channel.js, synth functions in
// /lib/audioEngine/synth.js, the metronome in /lib/audioEngine/scheduler.js.
// FX recipes have been in /lib/audioEngine/fxRecipes.js since iter-58. The
// AudioEngine class below orchestrates everything; each module owns its
// domain. Re-exports below preserve the original public surface so callers
// like `import { Channel, downsample, EFFECT_KEYS } from "@/lib/audioEngine"`
// keep working unchanged.
export { EFFECT_KEYS, FX_PARAM_DEFAULTS, FX_PARAM_SCHEMA, Channel, downsample };


/** Derive the AudioWorkletProcessor name from a URL when not explicitly given.
 *  Convention: file basename without extension → e.g. "bitcrusher-processor.js"
 *  → "bitcrusher-processor".  Plugin authors who register with a different
 *  internal name MUST pass `opts.processorName` to `loadPluginToChannel`. */
function _processorNameFromUrl(url) {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.href : "http://localhost");
    const base = (u.pathname.split("/").pop() || "").replace(/\.js$/i, "");
    return base || "processor";
  } catch {
    return "processor";
  }
}


export class AudioEngine {
  static _pluginSeq = 0;
  constructor(channelCount = 64) {
    this.ctx = new (window.AudioContext || window.webkitAudioContext)();
    this.masterGain = this.ctx.createGain();
    this.masterGain.gain.value = 0.82;

    this.limiter = this.ctx.createDynamicsCompressor();
    this.limiter.threshold.value = -6;
    this.limiter.knee.value = 0;
    this.limiter.ratio.value = 20;
    this.limiter.attack.value = 0.003;
    this.limiter.release.value = 0.25;

    this.masterAnalyser = this.ctx.createAnalyser();
    this.masterAnalyser.fftSize = 512;
    this.masterPeakBuffer = new Uint8Array(this.masterAnalyser.frequencyBinCount);

    // Real mastering chain — sits BETWEEN masterGain and the safety limiter.
    // Identity by default (0 dB / 1:1); runMastering() activates the corrective chain.
    this.masteringChain = buildMasteringChain(this.ctx, this.masterGain, this.limiter);

    this.limiter.connect(this.masterAnalyser);
    this.masterAnalyser.connect(this.ctx.destination);

    // Mastering state — last analysis + applied recipe (for UI display)
    this.lastMastering = null;

    // Shared reverb IR — generated once, reused across all 64 channels (saves ~14M random calls)
    this.sharedReverbIR = makeReverbIR(this.ctx);

    this.channels = Array.from({ length: channelCount }, () => new Channel(this.ctx, this.masterGain, this.sharedReverbIR));
    this.playing = false;
    this.startTime = 0;
    this.position = 0;
    this.maxDuration = 0;

    // Loop region (used by the rAF tick driver — actual wrap is handled by the UI loop
    // so we don't need a separate ScriptProcessor here).
    this.loopEnabled = false;
    this.loopStart = 0;
    this.loopEnd = 0;

    // Recording
    this.recorder = null;
    this.recordingChunks = [];
    this.recordingStream = null;

    // Multi-track tracking session — when the user "arms" N channels,
    // pressing Record fires up ONE getUserMedia stream from the chosen
    // device, splits it into per-channel mono recorders, and stops them
    // all when the user clicks Stop Tracking.
    //   armedChannels: Map<channelIdx, sourceInputChannel>
    //   inputDeviceId: string|null (defaults to browser-chosen)
    //   trackingSession: { stream, splitter, sourceNode, recorders[] } | null
    this.armedChannels = new Map();
    this.inputDeviceId = null;
    this.trackingSession = null;

    // Tempo + metronome + dry mode — wired to per-channel FX so changes are audible.
    // iter-72 — the metronome state + scheduler timer moved to a dedicated
    // Metronome class (lib/audioEngine/scheduler.js). The engine still owns
    // the tempo, but the click loop is now self-contained.
    this.tempo = 120;
    // Start in Dry mode by default — clean output, no reverb wet tail unless
    // the user explicitly turns reverb on per channel AND flips Wet mode.
    // This avoids the "everything sounds washed out" first-listen impression.
    this.dryMode = true;
    this.metronome = new Metronome(this.ctx, () => this.tempo);

    // ─── Collab broadcast hook (iter 33) ───────────────────────────────
    // Parent attaches a callback here. Every channel-level mutation (volume,
    // pan, mute, solo) fires it with a typed event so the WebSocket relay can
    // sync peers. Set to null when no collab is active — these mutations are
    // hot-path so we guard the dispatch with a single null check.
    this.onCollabEvent = null;
    // When applying an INCOMING peer change, we set this flag to avoid
    // re-broadcasting it. The setter wrappers check this before firing.
    this._collabSilent = false;
  }

  /** Set session tempo in BPM. Tempo-syncs the delay FX time on every channel
   *  to a dotted-eighth note (3/16ths of a beat) which is the most musically
   *  useful tempo-locked delay length. Also rebases the metronome schedule
   *  if it's running. */
  setTempo(bpm) {
    const clamped = Math.max(40, Math.min(280, Math.round(bpm)));
    this.tempo = clamped;
    const secondsPerBeat = 60 / clamped;
    const delayTime = secondsPerBeat * 0.75; // dotted-eighth
    const t = this.ctx.currentTime;
    this.channels.forEach((c) => {
      if (c.fxChain?.nodes?.delay) {
        c.fxChain.nodes.delay.delayTime.setTargetAtTime(delayTime, t, 0.05);
      }
      // Phase-(c) — push the new beat duration into every channel so clip
      // scheduling stays correct when the tempo changes mid-session.
      c.engineSecPerBeat = secondsPerBeat;
    });
    // If metronome is running, reset its scheduling clock to the new tempo.
    this.metronome.rebase();
    return clamped;
  }

  /** Master "Dry / Real" mode — zeros the reverb + delay wet sends on every
   *  channel without touching the per-channel FX toggles, so the user can
   *  flip back to "wet" instantly. Returns the new state. */
  setDryMode(enabled) {
    this.dryMode = !!enabled;
    const t = this.ctx.currentTime;
    this.channels.forEach((c) => {
      if (!c.fxChain?.nodes) return;
      const { reverbSend, delaySend, reverbWet, delayWet } = c.fxChain.nodes;
      if (enabled) {
        // Hard-zero the wet returns — wet/dry sends still respect their
        // own state but nothing makes it past the wet bus to the mixer.
        reverbWet?.gain.setTargetAtTime(0, t, 0.02);
        delayWet?.gain.setTargetAtTime(0, t, 0.02);
      } else {
        reverbWet?.gain.setTargetAtTime(1, t, 0.02);
        delayWet?.gain.setTargetAtTime(1, t, 0.02);
      }
    });
    return this.dryMode;
  }

  // ─── Metronome (delegates to lib/audioEngine/scheduler.js) ────────────
  // iter-72 — the scheduling loop + state moved to a Metronome instance.
  // The engine just forwards calls so existing UI code (`engine.startMetronome()`,
  // `engine.metronomeOn`) keeps working unchanged.
  startMetronome() { this.metronome.start(); }
  stopMetronome() { this.metronome.stop(); }
  toggleMetronome() { return this.metronome.toggle(); }
  /** Back-compat alias — UI mirror reads engine.metronomeOn to render the
   *  toolbar indicator. Defined as a getter so it always reflects the live
   *  metronome state regardless of how it was toggled. */
  get metronomeOn() { return this.metronome.on; }

  // ─── MIDI synth (delegates to lib/audioEngine/synth.js) ────────────────
  // iter-72 — the 3-osc voice + offline render functions moved out of the
  // engine. We keep instance methods here so MidiClipEditor + tests can
  // still call them via the engine handle (no churn to callers).
  previewMidiNote(pitch, duration = 0.4, velocity = 100, waveform = "sawtooth") {
    previewMidiNote(this.ctx, pitch, duration, velocity, waveform);
  }

  async renderMidiClipToBuffer(clip) {
    return renderMidiClipToBuffer(this.ctx?.sampleRate, clip);
  }

  /** @deprecated iter-72 — use scheduleSynthNote from @/lib/audioEngine/synth.
   *  Kept as a thin instance shim so engine code paths still resolve. */
  _scheduleSynthNote(audioCtx, dest, pitch, startTime, duration, velocity = 100, waveform = "sawtooth") {
    scheduleSynthNote(audioCtx, dest, pitch, startTime, duration, velocity, waveform);
  }

  anySolo() {
    return this.channels.some((c) => c.solo && c.buffer);
  }

  recomputeMix() {
    const anySolo = this.anySolo();
    this.channels.forEach((c) => c.applyMix(anySolo));
  }

  /** Helper: fire the collab broadcast hook unless we're applying an incoming
   *  peer change (would cause an infinite echo loop). */
  _emitCollab(type, payload) {
    if (this._collabSilent || !this.onCollabEvent) return;
    try { this.onCollabEvent({ type, payload }); }
    catch (e) { /* never crash audio path on a broadcast hook bug */ void e; }
  }

  setChannelVolume(i, v) {
    this.channels[i].setVolume(v, this.anySolo());
    this._emitCollab("mixer", { channel: i, field: "volume", value: v });
  }
  setChannelPan(i, p) {
    this.channels[i].setPan(p);
    this._emitCollab("mixer", { channel: i, field: "pan", value: p });
  }
  setChannelMute(i, m) {
    this.channels[i].muted = m;
    // iter-77 — propagate mute through the group so the whole lineage
    // mutes/unmutes together. Skip if no group (vast majority of channels).
    const groupId = this.channels[i].groupId;
    if (groupId) {
      this.channels.forEach((c, idx) => {
        if (idx !== i && c.groupId === groupId) c.muted = m;
      });
    }
    this.recomputeMix();
    this._emitCollab("mixer", { channel: i, field: "muted", value: !!m });
  }
  setChannelSolo(i, s) {
    this.channels[i].solo = s;
    // iter-77 — propagate solo through the group. Same rationale as mute:
    // soloing one node in a derivation lineage should solo the whole chain.
    const groupId = this.channels[i].groupId;
    if (groupId) {
      this.channels.forEach((c, idx) => {
        if (idx !== i && c.groupId === groupId) c.solo = s;
      });
    }
    this.recomputeMix();
    this._emitCollab("mixer", { channel: i, field: "solo", value: !!s });
  }

  /** iter-77 — Bind a channel to a named group. Channels sharing a groupId
   *  link their mute and solo state (and render a colored stripe in the
   *  mixer UI). Pass `groupId=null` to unbind.
   *
   *  Used by `SongLibrary.handleBounceLineage` to bind every channel
   *  loaded from a derivation lineage to the same group so the user can
   *  A/B the whole chain with one click of any group member's Solo button.
   */
  setChannelGroup(i, groupId, groupColor) {
    if (!this.channels[i]) return;
    this.channels[i].groupId = groupId || null;
    this.channels[i].groupColor = groupColor || null;
    this._emitCollab("mixer", { channel: i, field: "group", value: { id: groupId, color: groupColor } });
  }

  /** Real-time per-channel pitch bend (semitones). Updates the underlying
   *  AudioBufferSourceNode.detune param immediately, persists on the channel
   *  for newly-started sources, and broadcasts via CRDT so collaborators see
   *  the move in real time. */
  setChannelPitchBend(i, semi) {
    const ch = this.channels[i];
    if (!ch) return;
    ch.setPitchBend(semi);
    this._emitCollab("mixer", { channel: i, field: "pitchBendSemi", value: ch.pitchBendSemi });
  }

  /** WAM v2-lite plugin descriptor for a loaded plugin. Mirrors a subset of
   *  the Web Audio Modules v2 spec — `name`, `vendor`, `version`, `paramsInfo`
   *  (key → {min, max, default}), `hasAudioInput`, `hasAudioOutput`. Full
   *  WAM v2 strict compliance (createGroup, MIDI ports, host adapter) ships
   *  in Phase 2b. */
  getPluginDescriptor(channelIdx, pluginId) {
    const ch = this.channels[channelIdx];
    if (!ch) return null;
    const entry = ch.plugins.find((p) => p.id === pluginId);
    if (!entry) return null;
    return {
      id: entry.id,
      name: entry.label || entry.processorName,
      vendor: "ELPM Studio",
      version: "1.0",
      processorName: entry.processorName,
      hasAudioInput: true,
      hasAudioOutput: true,
      paramsInfo: { ...(entry.params || {}) },
      url: entry.url,
    };
  }

  /** WAM v2-lite getState — snapshot of every param value, suitable for
   *  saving as a named preset. */
  getPluginState(channelIdx, pluginId) {
    const ch = this.channels[channelIdx];
    if (!ch) return null;
    const entry = ch.plugins.find((p) => p.id === pluginId);
    if (!entry) return null;
    return { ...(entry.params || {}) };
  }

  /** WAM v2-lite setState — applies an entire param object to a loaded plugin.
   *  Updates the local `entry.params` mirror AND posts every key to the
   *  worklet via setPluginParam (so the audio side sees the change). */
  setPluginState(channelIdx, pluginId, state) {
    const ch = this.channels[channelIdx];
    if (!ch) return;
    const entry = ch.plugins.find((p) => p.id === pluginId);
    if (!entry) return;
    for (const [k, v] of Object.entries(state || {})) {
      this.setPluginParam(channelIdx, pluginId, k, v);
    }
  }

  // ─── Plugin host (AudioWorklet-based, tempo-synced) ─────────────────
  // Each plugin is an AudioWorkletNode that's appended to a channel's signal
  // path between the FX chain output and the channel's gainNode. Plugins
  // receive real-time transport ticks via port.postMessage({type:"transport",
  // currentTime, tempo, beat, bar}) so they can sync rate-locked effects.

  /** Insert an AudioWorklet plugin into a channel. `processorUrl` is the URL
   *  of a static .js file that registers an AudioWorkletProcessor. Returns
   *  the assigned plugin id (string) on success, throws on failure. */
  async loadPluginToChannel(channelIdx, processorUrl, opts = {}) {
    const ch = this.channels[channelIdx];
    if (!ch) throw new Error(`channel ${channelIdx} not found`);
    if (!this.ctx.audioWorklet) throw new Error("AudioWorklet not supported in this browser");
    // Idempotent module registration — addModule is safe to re-call but we
    // dedupe via a set to avoid pointless network round-trips.
    this._loadedWorkletUrls = this._loadedWorkletUrls || new Set();
    if (!this._loadedWorkletUrls.has(processorUrl)) {
      await this.ctx.audioWorklet.addModule(processorUrl);
      this._loadedWorkletUrls.add(processorUrl);
    }
    const processorName = opts.processorName || _processorNameFromUrl(processorUrl);
    let node;
    try {
      node = new AudioWorkletNode(this.ctx, processorName, {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [2],
      });
    } catch (e) {
      throw new Error(`Failed to instantiate worklet "${processorName}": ${e.message}`);
    }
    const id = `plugin_${++AudioEngine._pluginSeq}`;
    const entry = {
      id,
      url: processorUrl,
      label: opts.label || processorName,
      processorName,
      node,
      params: {},
    };
    ch.plugins.push(entry);
    this._rewirePluginChain(channelIdx);
    // Immediately push the current transport state so the plugin starts in sync.
    try {
      node.port.postMessage({
        type: "transport",
        currentTime: this.ctx.currentTime,
        tempo: this.tempo || 120,
        beat: this._currentBeat() || 0,
        bar: Math.floor((this._currentBeat() || 0) / 4),
        playing: !!this.playing,
      });
    } catch (e) { void e; }
    this._emitCollab("plugin", { channel: channelIdx, action: "load", id, url: processorUrl, processorName, label: entry.label });
    return id;
  }

  /** Remove a plugin by id. Disconnects the node + re-wires the channel. */
  removePluginFromChannel(channelIdx, pluginId) {
    const ch = this.channels[channelIdx];
    if (!ch) return;
    const idx = ch.plugins.findIndex((p) => p.id === pluginId);
    if (idx < 0) return;
    const [entry] = ch.plugins.splice(idx, 1);
    try { entry.node.disconnect(); } catch (e) { void e; }
    try { entry.node.port.close?.(); } catch (e) { void e; }
    this._rewirePluginChain(channelIdx);
    this._emitCollab("plugin", { channel: channelIdx, action: "remove", id: pluginId });
  }

  /** Send a parameter update to a loaded plugin. Plugins receive these via
   *  their port.onmessage handler ({type:"param", key, value}). */
  setPluginParam(channelIdx, pluginId, key, value) {
    const ch = this.channels[channelIdx];
    if (!ch) return;
    const entry = ch.plugins.find((p) => p.id === pluginId);
    if (!entry) return;
    entry.params[key] = value;
    try { entry.node.port.postMessage({ type: "param", key, value }); } catch (e) { void e; }
    this._emitCollab("plugin", { channel: channelIdx, action: "param", id: pluginId, key, value });
  }

  /** Internal: rebuild the per-channel signal chain after a plugin add/remove.
   *  Routes FxChain.output → plugin[0] → plugin[1] → ... → fxChain.terminal
   *  (which is `sidechainGain` for non-empty plugin chains, or just the same
   *  default wiring when no plugins are loaded). Empty chain restores the
   *  pre-plugin direct wire FxChain.output → fxChain.terminal. */
  _rewirePluginChain(channelIdx) {
    const ch = this.channels[channelIdx];
    if (!ch) return;
    // Disconnect the FxChain output's prior wiring + every plugin node.
    try { ch.fxChain.output.disconnect(); } catch (e) { void e; }
    for (const p of ch.plugins) {
      try { p.node.disconnect(); } catch (e) { void e; }
    }
    // Reconnect FxChain.output → plugin0 → plugin1 → ... → fxChain.terminal
    let prev = ch.fxChain.output;
    for (const p of ch.plugins) {
      try { prev.connect(p.node); } catch (e) { void e; }
      prev = p.node;
    }
    try { prev.connect(ch.fxChain.terminal); } catch (e) { void e; }
  }

  /** Broadcast a transport tick to every loaded plugin on every channel.
   *  Called from the engine's render-loop / transport advance. Plugins use
   *  this to lock their internal rate (e.g. bitcrusher bit-depth at 1/4 beat
   *  intervals) to the DAW's tempo and beat position. Throttled internally —
   *  the rAF caller can be naive about timing. */
  _broadcastTransportTick() {
    if (!this.channels || !this.channels.length) return;
    // Throttle to ~12 Hz (every 4 frames at 50Hz). Plugins use their own
    // sample-rate internal counters; this is just for tempo + beat sync.
    const now = this.ctx.currentTime;
    if (this._lastPluginTickAt && (now - this._lastPluginTickAt) < 0.08) return;
    this._lastPluginTickAt = now;
    const msg = {
      type: "transport",
      currentTime: now,
      tempo: this.tempo || 120,
      beat: this._currentBeat() || 0,
      bar: Math.floor((this._currentBeat() || 0) / 4),
      playing: !!this.playing,
    };
    for (const ch of this.channels) {
      for (const p of (ch.plugins || [])) {
        try { p.node.port.postMessage(msg); } catch (e) { void e; }
      }
    }
  }

  /** Returns the current beat position (transport time × tempo / 60). Returns
   *  0 when transport hasn't started. Defensive — used by both transport-tick
   *  broadcast and the plugin instantiation initial-state sync. */
  _currentBeat() {
    if (!this.playing || !this.startTime) return 0;
    const elapsed = this.ctx.currentTime - this.startTime;
    return Math.max(0, elapsed) * (this.tempo || 120) / 60;
  }

  setChannelEffect(i, name, enabled) {
    this.channels[i].setEffect(name, enabled);
  }

  /* ─── Sidechain compression (iter 34) ─────────────────────────────────
   * Web Audio's DynamicsCompressorNode doesn't support a separate sidechain
   * input, so we implement ducking via envelope-driven gain modulation:
   * read the trigger channel's analyser peak each frame, and ramp the target
   * channel's sidechainGain toward (1 - depth) when peak > threshold or
   * back to 1.0 in release time. setTargetAtTime gives us smooth exponential
   * envelopes without zipper noise — same approach the native compressor
   * uses internally for its knee. */
  setSidechain(targetIdx, patch) {
    const ch = this.channels[targetIdx];
    if (!ch || !patch || typeof patch !== "object") return;
    Object.assign(ch.sidechain, patch);
    // When switching to LFO mode OR clearing keyChannel in audio mode, snap
    // the gain back to 1.0 so we don't leave the channel stuck ducked.
    const switchingToLfoFromAudio = patch.mode === "lfo";
    const clearingAudioTrigger = patch.keyChannel == null && "keyChannel" in patch && ch.sidechain.mode === "audio";
    if (switchingToLfoFromAudio || clearingAudioTrigger) {
      ch.sidechainGain.gain.cancelScheduledValues(this.ctx.currentTime);
      ch.sidechainGain.gain.setTargetAtTime(1, this.ctx.currentTime, 0.05);
    }
    this._emitCollab("sidechain", { channel: targetIdx, ...patch });
  }

  getSidechain(idx) {
    return this.channels[idx]?.sidechain || null;
  }

  /** Per-frame: drive every armed channel's sidechainGain based on its trigger
   *  source. Two modes:
   *   - "audio": read the trigger channel's getPeak() (kick → bass ducking)
   *   - "lfo":   tempo-synced rhythmic duck (no trigger channel needed; phase
   *              follows ctx.currentTime % beatInterval). Use this when you
   *              want the EDM-style "pumping pad" without sacrificing a kick channel.
   *  Cost scales with active sidechain count, not total channel count. */
  tickSidechain() {
    const t = this.ctx.currentTime;
    // Beat interval drives the LFO phase. Tempo defaults to 120; we cache the
    // calculation per tick rather than per channel.
    const beatSec = 60 / Math.max(20, this.tempo);
    for (let i = 0; i < this.channels.length; i++) {
      const ch = this.channels[i];
      const sc = ch.sidechain;
      // Determine if this channel is armed and which signal source to use.
      let triggerHigh = false;
      if (sc.mode === "lfo") {
        // Quarter-note phase within the sub-division. We treat the FIRST 1/8th
        // of the beat as the "trigger pulse" — gives a snappy duck/release that
        // mimics a real kick envelope. Adjust by halving the duty-cycle if the
        // depth is very high (long-tail ducks need less ON time).
        const subSec = beatSec * sc.lfoSubdivision;
        const phase = (t % subSec) / subSec;
        triggerHigh = phase < 0.125; // 12.5% duty cycle
      } else {
        // Audio mode (default) — only armed when a keyChannel is set + the
        // trigger channel has a buffer loaded.
        if (sc.keyChannel == null) continue;
        const trigger = this.channels[sc.keyChannel];
        if (!trigger || !trigger.buffer) continue;
        triggerHigh = trigger.getPeak() > sc.threshold;
      }
      if (triggerHigh) {
        ch.sidechainGain.gain.setTargetAtTime(Math.max(0.0, 1 - sc.depth), t, sc.attack);
      } else {
        ch.sidechainGain.gain.setTargetAtTime(1.0, t, sc.release);
      }
    }
  }

  /** Update a single FX param on a specific channel. Used by the Effects Rack
   *  knob UI; runs through the same setTargetAtTime smoothing as on/off so
   *  there's no audible click as the user drags.
   *
   *  If the engine is playing AND this channel is armed for automation AND we
   *  aren't currently replaying automation (else we'd infinite-record), the
   *  change gets appended to the channel's automation lane. */
  setChannelEffectParam(i, name, key, value) {
    const ch = this.channels[i];
    if (!ch) return;
    ch.setEffectParam(name, key, value);
    if (this.playing && ch.automationArmed && !this._replayingAutomation) {
      ch.recordAutomation(name, key, this.getPlayhead(), value);
    }
    // Broadcast to collab peers — gated by _collabSilent to prevent ping-pong.
    this._emitCollab("fx_param", { channel: i, effect: name, param: key, value });
  }

  /** Toggle automation recording on a channel.
   *  Newly-armed channels start clean at the next setEffectParam during playback
   *  (no auto-clear here — caller can choose to clearChannelAutomation explicitly). */
  setChannelAutomationArm(i, armed) {
    const ch = this.channels[i];
    if (ch) ch.automationArmed = !!armed;
  }

  clearChannelAutomation(i, name, key) {
    this.channels[i]?.clearAutomation(name, key);
  }

  getChannelAutomationSummary(i) {
    return this.channels[i]?.getAutomationSummary() || { armed: false, totalPoints: 0, params: [] };
  }

  /** Per-(effect, param) lane data for the selected channel. Returns:
   *    [{ name, key, points: [{t, v}, ...], min, max }, ...]
   *  - Used by the SideCards "Per-param lanes" panel to render mini-sparklines.
   *  - Sorted by total point count descending so the most-active lanes appear first.
   *  - We re-derive min/max here rather than caching, because the lane arrays
   *    grow incrementally and an absolute pass is O(n) per render anyway. */
  getChannelAutomationLanes(i) {
    const ch = this.channels[i];
    if (!ch || !ch.automation) return [];
    const lanes = [];
    for (const [name, paramGroup] of Object.entries(ch.automation)) {
      for (const [key, pts] of Object.entries(paramGroup)) {
        if (!pts.length) continue;
        let min = Infinity, max = -Infinity;
        for (const p of pts) {
          if (p.v < min) min = p.v;
          if (p.v > max) max = p.v;
        }
        lanes.push({ name, key, points: pts, min, max, count: pts.length });
      }
    }
    lanes.sort((a, b) => b.count - a.count);
    return lanes;
  }

  /** Per-frame replay tick. Walks every channel's automation lanes and applies
   *  any newly-passed points. The internal `_replayingAutomation` flag prevents
   *  the apply() call from being mistaken for a user-driven knob change. */
  tickAutomation() {
    if (!this.playing) return;
    const t = this.getPlayhead();
    this._replayingAutomation = true;
    try {
      for (let i = 0; i < this.channels.length; i++) {
        const ch = this.channels[i];
        if (!ch || !Object.keys(ch.automation).length) continue;
        ch.replayAutomation(t, (name, key, value) => {
          // Apply directly via the channel — avoids re-routing through
          // setChannelEffectParam's record-guard branch.
          ch.setEffectParam(name, key, value);
        });
      }
    } finally {
      this._replayingAutomation = false;
    }
  }

  getChannelFx(i) {
    return { ...this.channels[i].fx };
  }

  /** Read the current per-channel FX params (deep clone so callers can't
   *  accidentally mutate engine state). Returns null for invalid channel. */
  getChannelFxParams(i) {
    const ch = this.channels[i];
    if (!ch) return null;
    const out = {};
    for (const k of Object.keys(ch.fxParams)) out[k] = { ...ch.fxParams[k] };
    return out;
  }

  setMasterVolume(v) {
    this.masterGain.gain.setTargetAtTime(v / 100, this.ctx.currentTime, 0.01);
  }
  setLimiterCeiling(v) {
    // 0..100 mapped to threshold -24..0 dB
    const th = -24 + (v / 100) * 24;
    this.limiter.threshold.setValueAtTime(th, this.ctx.currentTime);
  }

  async loadFileToChannel(idx, file) {
    if (this.ctx.state === "suspended") await this.ctx.resume();
    const buf = await this.channels[idx].loadFile(file);
    this.maxDuration = Math.max(this.maxDuration, buf.duration);
    return buf;
  }

  loadBufferToChannel(idx, buf, name, sourceUrl = null, _fromStretch = false) {
    this.channels[idx].loadBuffer(buf, name, sourceUrl);
    this.maxDuration = Math.max(this.maxDuration, buf.duration);
    // iter-265 — Any fresh load INVALIDATES the pristine-stretch cache:
    // the user has replaced the audio so any previous pristine ref is
    // now stale. Stretch operations bypass this wipe via `_fromStretch`.
    if (!_fromStretch && this._pristineBuffers?.has?.(idx)) {
      this._pristineBuffers.delete(idx);
    }
  }

  // ─── Phase-(c) — Clip arrangement API ──────────────────────────────
  // Thin pass-throughs to Channel.{add,move,remove}Clip. Centralised here so
  // the UI can call `engine.addClipToChannel(...)` without reaching into
  // the channel object directly.

  /** Add a clip at a specific beat position. Returns the new clip id. */
  addClipToChannel(idx, buf, startBeat = 0, name = "Clip", durationBeats = null) {
    const ch = this.channels[idx];
    if (!ch) return null;
    const id = ch.addClip(buf, startBeat, name, durationBeats);
    // Extend the maxDuration so the bar grid + transport scrubber size right.
    const secPerBeat = 60 / (this.tempo || 120);
    const clipEnd = startBeat * secPerBeat + (buf?.duration || 0);
    this.maxDuration = Math.max(this.maxDuration, clipEnd);
    return id;
  }

  /** Move a clip on a given channel. Re-schedules live if transport is playing. */
  moveClipOnChannel(idx, clipId, newStartBeat) {
    const ch = this.channels[idx];
    if (!ch) return;
    ch.moveClip(clipId, newStartBeat);
    if (this.playing) {
      const elapsed = this.ctx.currentTime - this.startTime;
      ch._scheduleClips(this.ctx.currentTime, elapsed, ch.engineSecPerBeat);
    }
  }

  /** Remove a clip. Returns true on success. */
  removeClipFromChannel(idx, clipId) {
    const ch = this.channels[idx];
    if (!ch) return false;
    const ok = ch.removeClip(clipId);
    if (ok && this.playing) {
      const elapsed = this.ctx.currentTime - this.startTime;
      ch._scheduleClips(this.ctx.currentTime, elapsed, ch.engineSecPerBeat);
    }
    return ok;
  }

  /** Flat snapshot of every clip across every channel, for the arrangement view. */
  getAllClips() {
    const out = [];
    for (let i = 0; i < this.channels.length; i++) {
      const ch = this.channels[i];
      if (!ch?.clips?.length) continue;
      for (const c of ch.clips) {
        out.push({
          channelIdx: i,
          id: c.id,
          startBeat: c.startBeat,
          durationBeats: c.durationBeats,
          durationSec: c.buffer?.duration || 0,
          name: c.name,
        });
      }
    }
    return out;
  }

  /** `at` (optional) = audio-clock time to start at, e.g. right after a count-in. */
  async play(at) {
    if (this.ctx.state === "suspended") await this.ctx.resume();
    if (this.playing) return;
    const when = Math.max(this.ctx.currentTime + 0.05, at || 0);
    this.channels.forEach((c) => c.start(when, this.position));
    this.startTime = when - this.position;
    // iter-237 — wall-clock timestamp of the play moment. Used by the
    // Bridge "Tap-to-tune" feature to compute phase offset between
    // user taps (which they sync to Ableton's audio) and ELPM's beat
    // grid (BPM × tempo derived from this.tempo).
    this.playStartedAtMs = Date.now() + (when - this.ctx.currentTime) * 1000;
    this.playing = true;
    // Align replay cursors with the play offset so automation fires from the right
    // point even when resuming mid-session.
    this.channels.forEach((c) => c.resetAutomationCursor(this.position));
    this._emitCollab("transport", { action: "play", position: this.position });
  }

  pause() {
    if (!this.playing) return;
    this.position = this.ctx.currentTime - this.startTime;
    this.channels.forEach((c) => c.stop());
    this.playing = false;
    this._emitCollab("transport", { action: "pause", position: this.position });
  }

  stop() {
    this.channels.forEach((c) => c.stop());
    this.position = 0;
    this.playing = false;
    // Re-arm replay from the top for the next play.
    this.channels.forEach((c) => c.resetAutomationCursor(0));
    this._emitCollab("transport", { action: "stop", position: 0 });
  }

  getPlayhead() {
    if (this.playing) return this.ctx.currentTime - this.startTime;
    return this.position;
  }

  /** Jump the playhead to `time` (seconds). If currently playing, restart all
   *  channel sources at the new offset. Returns the clamped target time. */
  seekTo(time) {
    const target = Math.max(0, Math.min(this.maxDuration || time, time));
    if (this.playing) {
      this.channels.forEach((c) => c.stop());
      const when = this.ctx.currentTime + 0.02;
      this.channels.forEach((c) => c.start(when, target));
      this.startTime = when - target;
    } else {
      this.position = target;
    }
    // Re-align automation replay cursors with the new playhead so the next
    // tickAutomation() fires from the correct starting point.
    this.channels.forEach((c) => c.resetAutomationCursor(target));
    this._emitCollab("transport", { action: "seek", position: target });
    return target;
  }

  /** Configure the loop region. Pass `enabled=false` to disable. The actual
   *  loop wrap is performed by the rAF tick consumer — this just stores the
   *  parameters and the consumer calls seekTo(loopStart) when playhead crosses
   *  loopEnd. */
  setLoop(enabled, start = 0, end = null) {
    this.loopEnabled = !!enabled;
    this.loopStart = Math.max(0, start);
    this.loopEnd = end == null ? (this.maxDuration || 0) : Math.max(this.loopStart + 0.05, end);
  }

  /** Auto-loop sync — flip every loaded channel into internal-loop mode so
   *  each buffer naturally repeats, then set the global loop region to
   *  `targetBars` bars at the current tempo. If targetBars is "auto", uses
   *  the LONGEST loaded buffer's duration rounded to the nearest bar.
   *
   *  Effect: all channels stay in lockstep regardless of their natural lengths.
   */
  autoSyncChannels({ targetBars = "auto" } = {}) {
    const secondsPerBeat = 60 / (this.tempo || 120);
    const secondsPerBar = secondsPerBeat * 4;
    // Determine loop length.
    let loopSeconds;
    if (targetBars === "auto") {
      const longest = Math.max(
        0,
        ...this.channels.filter((c) => c.buffer).map((c) => c.buffer.duration),
      );
      const longestBars = Math.max(1, Math.round(longest / secondsPerBar));
      loopSeconds = longestBars * secondsPerBar;
    } else {
      loopSeconds = Math.max(1, Number(targetBars)) * secondsPerBar;
    }
    // Enable internal loop on every channel that has a buffer.
    this.channels.forEach((c) => {
      if (c.buffer) c.internalLoop = true;
    });
    this.setLoop(true, 0, loopSeconds);
    // Restart playback so the new `source.loop = true` setting takes effect on
    // currently-streaming source nodes (Web Audio doesn't let you flip loop
    // dynamically on an already-started source).
    if (this.playing) {
      const t = this.ctx.currentTime;
      this.channels.forEach((c) => c.start(t, this.position));
    }
    return loopSeconds;
  }

  /** Quantize a single channel — adjust its `playbackRate` so the buffer fits
   *  exactly `targetBars` bars at the current tempo. Mode 'pitch' uses
   *  src.playbackRate (changes pitch with speed); 'preserve' would require a
   *  proper time-stretch lib so we stick with pitch-mode for now. */
  quantizeChannel(channelIdx, targetBars = 4) {
    const c = this.channels[channelIdx];
    if (!c?.buffer) return null;
    const secondsPerBeat = 60 / (this.tempo || 120);
    const secondsPerBar = secondsPerBeat * 4;
    const target = secondsPerBar * Math.max(0.25, Number(targetBars));
    const rate = c.buffer.duration / target;
    // Clamp to a sensible range so we don't get pitch-blast.
    c.playbackRate = Math.max(0.25, Math.min(4, rate));
    c.internalLoop = true;
    // If currently playing, restart this channel with the new rate.
    if (this.playing) c.start(this.ctx.currentTime, this.position);
    return { rate: c.playbackRate, targetSeconds: target };
  }

  /** iter-265 — Pitch-preserving time-stretch on a single channel.
   *  Uses the SOLA algorithm in `lib/tempoDetection.timeStretch` to
   *  render a new AudioBuffer at `factor`× the original length WITHOUT
   *  changing the pitch (unlike `quantizeChannel` above, which uses
   *  playbackRate and shifts pitch with speed).
   *
   *  Behaviour:
   *   • The channel's CURRENT buffer at the moment of the FIRST stretch
   *     call is cached as the pristine reference. Subsequent stretches
   *     always render from pristine — so dragging the slider back and
   *     forth never compounds SOLA artefacts.
   *   • factor === 1.0 (or within 1 % of it) restores the pristine
   *     buffer without re-rendering.
   *   • Async (renders offline through OfflineAudioContext). Caller
   *     should debounce slider drags so we don't queue 30 stretches
   *     per second.
   *   • If the channel is playing, we restart the source on the new
   *     buffer so the user hears the stretched audio immediately.
   *   • Stores `stretchFactor` on the channel so the UI can read it
   *     back (initial render after page reload, save-session payload).
   *
   *  Returns: { factor, originalDuration, newDuration } on success,
   *           null when the channel is empty.
   */
  async stretchChannel(channelIdx, factor) {
    const c = this.channels[channelIdx];
    if (!c?.buffer) return null;
    // First call ever for this channel → cache the pristine buffer
    // BEFORE we mutate it. `_pristineBuffers` is the source-of-truth
    // every later stretch renders from.
    if (!this._pristineBuffers.has(channelIdx)) {
      this._pristineBuffers.set(channelIdx, {
        buffer: c.buffer,
        name: c.name,
        sourceUrl: c.sourceUrl || null,
      });
    }
    const pristine = this._pristineBuffers.get(channelIdx);
    const clamped = Math.max(0.5, Math.min(2.0, Number(factor) || 1));
    // Snap to 1.0× (no-stretch) for any factor within 1 % of unity —
    // avoids the SOLA pass altogether when the user double-clicks Reset.
    if (Math.abs(clamped - 1.0) < 0.01) {
      this.loadBufferToChannel(channelIdx, pristine.buffer, pristine.name, pristine.sourceUrl, true);
      const chRef = this.channels[channelIdx];
      if (chRef) chRef.stretchFactor = 1.0;
      // iter-265b — Also reset any scrub-preview playbackRate so we
      // play at the natural 1× speed of the pristine buffer.
      if (chRef && typeof chRef.setLivePlaybackRate === "function") {
        chRef.setLivePlaybackRate(1.0);
      } else if (chRef) {
        chRef.playbackRate = 1.0;
      }
      if (this.playing) chRef?.start(this.ctx.currentTime, this.position);
      return { factor: 1.0, originalDuration: pristine.buffer.duration, newDuration: pristine.buffer.duration };
    }
    // iter-265 — Use the statically-imported SOLA stretch.
    let stretched;
    try {
      stretched = await _tempoModule.timeStretch(pristine.buffer, clamped);
    } catch (e) {
      // Best-effort — don't blow up the engine on a bad render.
      // eslint-disable-next-line no-console
      console.warn(`stretchChannel(${channelIdx}, ${clamped}) failed:`, e);
      return null;
    }
    this.loadBufferToChannel(channelIdx, stretched, pristine.name, pristine.sourceUrl, true);
    const chRef = this.channels[channelIdx];
    if (chRef) chRef.stretchFactor = clamped;
    // iter-265b — The freshly-rendered buffer is ALREADY at the
    // target length (pitch preserved by SOLA), so we must reset any
    // playbackRate scaling that the scrub-preview applied — otherwise
    // the channel would play 2× as long again.
    if (chRef && typeof chRef.setLivePlaybackRate === "function") {
      chRef.setLivePlaybackRate(1.0);
    } else if (chRef) {
      chRef.playbackRate = 1.0;
    }
    if (this.playing) chRef?.start(this.ctx.currentTime, this.position);
    return {
      factor: clamped,
      originalDuration: pristine.buffer.duration,
      newDuration: stretched.duration,
    };
  }

  /** iter-265 — Convenience: stretch a channel so its NEW duration
   *  matches the current loop length exactly. Returns the same shape
   *  as stretchChannel + the requested factor. */
  async stretchChannelToLoop(channelIdx) {
    const c = this.channels[channelIdx];
    if (!c?.buffer) return null;
    const loopLen = Math.max(0, this.loopEnd - this.loopStart);
    if (loopLen <= 0) return null;
    const pristineBuf = this._pristineBuffers.get(channelIdx)?.buffer || c.buffer;
    const targetFactor = loopLen / pristineBuf.duration;
    return await this.stretchChannel(channelIdx, targetFactor);
  }

  /** iter-265 — Pristine-buffer cache, keyed by channel index. Stores
   *  the ORIGINAL buffer the user loaded into a channel before any
   *  stretch operation, so re-stretching never compounds quality loss.
   *  Cleared when the channel is cleared or replaced with a fresh load. */
  _pristineBuffers = new Map();

  /** iter-265b — Scrub-preview rate for the time-stretch slider.
   *  The slider calls this on every change for instant audible feedback
   *  (using src.playbackRate — pitch shifts with speed). When the user
   *  settles, `stretchChannel` lands a SOLA-rendered pitch-preserved
   *  buffer and resets playbackRate to 1.0.
   *
   *  Why TWO paths? Because the Web Audio API gives us free playback-
   *  rate scaling but cheats with pitch; the SOLA pass takes 50-200 ms
   *  to render and "kills" the live sound while running. By previewing
   *  live + landing the SOLA result on release, the slider feels both
   *  responsive AND musically correct.
   *
   *  factor → rate inverse: a 2.0× LONGER channel needs playbackRate
   *  0.5; a 0.5× shorter channel needs playbackRate 2.0.
   */
  setChannelPreviewStretchRate(channelIdx, factor) {
    const c = this.channels[channelIdx];
    if (!c) return null;
    const clamped = Math.max(0.5, Math.min(2.0, Number(factor) || 1));
    const rate = 1 / clamped;
    if (typeof c.setLivePlaybackRate === "function") c.setLivePlaybackRate(rate);
    else c.playbackRate = rate;
    return { factor: clamped, rate };
  }

  /** Turn off the auto-sync + quantization for every channel. Buffer plays at
   *  its natural rate and global loop falls back to maxDuration. */
  clearAutoSync() {
    this.channels.forEach((c) => {
      c.internalLoop = false;
      c.playbackRate = 1;
    });
    this.setLoop(this.loopEnabled, this.loopStart, this.maxDuration || this.loopEnd);
    if (this.playing) {
      const t = this.ctx.currentTime;
      this.channels.forEach((c) => c.start(t, this.position));
    }
  }

  getMasterPeak() {
    this.masterAnalyser.getByteTimeDomainData(this.masterPeakBuffer);
    let max = 0;
    for (let i = 0; i < this.masterPeakBuffer.length; i++) {
      const v = Math.abs(this.masterPeakBuffer[i] - 128);
      if (v > max) max = v;
    }
    return max / 128;
  }

  /** Recording mode controls whether the FX rack participates in capture:
   *    'dry'     — current behavior (default). Mic → MediaRecorder, FX untouched.
   *    'monitor' — Mic is ALSO routed through the channel's live FX chain so the
   *                user hears WET in their headphones while singing. The saved
   *                buffer stays DRY (sweetable later).
   *    'print'   — After capture, the dry buffer is rendered through the channel's
   *                FX in an OfflineAudioContext. The wet buffer replaces the dry
   *                in the channel; the dry is stashed in _undoCache so the user
   *                can revert with `engine.undoLastTrim(ch)` (same single-slot
   *                undo as silence-trim — destructive but reversible once). */
  setRecordingMode(mode) {
    if (!["dry", "monitor", "print"].includes(mode)) return false;
    this.recordingMode = mode;
    return true;
  }

  /** Render a dry buffer through the SAME FX chain configuration as channel `ch`.
   *  Used by the 'print' recording mode after the take is captured. Pure offline
   *  render — no main-thread audio interruption. Returns the wet AudioBuffer. */
  async _renderBufferThroughChannelFx(ch, dryBuffer) {
    const sourceCh = this.channels[ch];
    if (!sourceCh || !dryBuffer) return dryBuffer;
    // Skip work if every effect is off — print mode with no FX would just clone.
    const anyOn = Object.values(sourceCh.fx).some(Boolean);
    if (!anyOn) return dryBuffer;

    const sr = dryBuffer.sampleRate;
    const totalLen = dryBuffer.length;
    // Add a 0.5 s tail so reverb / delay decays don't get chopped.
    const tailLen = Math.floor(sr * 0.5);
    const offline = new OfflineAudioContext(dryBuffer.numberOfChannels || 1, totalLen + tailLen, sr);
    const sink = offline.createGain();
    sink.connect(offline.destination);
    const fxChain = buildFxChain(offline, sink, this.sharedReverbIR);
    EFFECT_KEYS.forEach((k) => fxChain.applyEffect(k, sourceCh.fx[k], sourceCh.fxParams[k]));
    const src = offline.createBufferSource();
    src.buffer = dryBuffer;
    src.connect(fxChain.input);
    src.start(0);
    return offline.startRendering();
  }

  /** Connect the live mic stream to the channel's FX chain so the user hears
   *  themselves wet while recording (monitor + print modes). Returns the
   *  monitor source node so the caller can disconnect it on stop. */
  _attachLiveMonitor(stream, channelIdx) {
    const ch = this.channels[channelIdx];
    if (!ch) return null;
    try {
      const monSrc = this.ctx.createMediaStreamSource(stream);
      monSrc.connect(ch.fxChain.input);
      return monSrc;
    } catch (e) {
      console.warn("[engine] live monitor attach failed:", e);
      return null;
    }
  }

  async startRecording(targetChannel, { deviceId = null } = {}) {
    // Never let a stuck audio device block the mic request — resume in the background after 1.5 s.
    if (this.ctx.state === "suspended") await Promise.race([this.ctx.resume(), new Promise((r) => setTimeout(r, 1500))]);
    // iter-147 — DAW-quality vocal capture. Chrome's defaults enable
    // echoCancellation + noiseSuppression + autoGainControl which are
    // designed for VoIP and DESTROY music vocals (notch filters carve
    // formants, AGC pumps dynamics, NS gates sustained notes). For a
    // DAW we want the raw mic signal.
    //
    // iter-150 fix — DO NOT pin sampleRate/sampleSize/channelCount.
    // On some Chrome/Linux/USB combos those strict-looking constraints
    // either fail outright or silently return an empty (zero-sample)
    // stream — the user's "no sound on playback" report after iter-147.
    // The browser already gives us native rate + channels for the
    // device, which is what we want anyway.
    const audioConstraints = {
      ...(deviceId ? { deviceId: { exact: deviceId } } : {}),
      echoCancellation: false,
      noiseSuppression: false,
      autoGainControl: false,
      // Google-prefixed legacy keys — older Chromium needs these to honour
      // the toggles even when the standard keys are present.
      googEchoCancellation: false,
      googAutoGainControl: false,
      googNoiseSuppression: false,
      googHighpassFilter: false,
      googTypingNoiseDetection: false,
    };
    const constraints = { audio: audioConstraints };
    try {
      this.recordingStream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (e) {
      // Last-resort fallback: some browsers reject ANY of our processing
      // toggles. Re-try with the bare minimum so the user still gets a
      // working recording (possibly with VoIP processing on).
      console.warn("[engine] getUserMedia strict failed, retrying minimal:", e);
      this.recordingStream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { exact: deviceId } } : true,
      });
    }

    // Log what the device actually settled on — useful when the user reports
    // a quality issue we can't reproduce. Chrome silently downgrades sample
    // rate on some USB interfaces, and our settings tell the story.
    try {
      const settings = this.recordingStream.getAudioTracks()[0]?.getSettings?.() || {};
      logger.info("[engine] mic capture settings:", {
        sampleRate: settings.sampleRate,
        channelCount: settings.channelCount,
        echoCancellation: settings.echoCancellation,
        noiseSuppression: settings.noiseSuppression,
        autoGainControl: settings.autoGainControl,
        deviceId: settings.deviceId,
      });
    } catch { /* settings.getSettings() not supported — non-fatal. */ }

    this.recordingChunks = [];
    // Attach live monitor for monitor + print modes so the user hears the FX
    // while singing. Pure additional Web Audio routing — does NOT touch the
    // MediaRecorder source which still gets the raw mic stream.
    const mode = this.recordingMode || "dry";
    if (mode === "monitor" || mode === "print") {
      this.recordingMonitorSrc = this._attachLiveMonitor(this.recordingStream, targetChannel);
    } else {
      this.recordingMonitorSrc = null;
    }
    // Build a tap analyser so the UI can show a live peak / scrolling waveform
    // for the channel that's being recorded into. The analyser is parallel to
    // the recorder — capture is unaffected.
    try {
      const src = this.ctx.createMediaStreamSource(this.recordingStream);
      const analyser = this.ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      src.connect(analyser);
      this.recordingAnalyser = { node: analyser, src, ch: targetChannel, startedAt: this.ctx.currentTime };
    } catch {
      this.recordingAnalyser = null;
    }

    // iter-147 — pick the highest-quality container the browser supports.
    // Default MediaRecorder bitrate is ~64 kbps (telephone quality). For
    // music we want 256 kbps Opus minimum (near-transparent for vocals).
    // Order: webm/opus 256k → webm 256k → mp4 (Safari) → default.
    //
    // iter-150 fix — wrap in try/catch and fall back to default bitrate
    // if a browser refuses 256 kbps. Some Safari/iOS builds throw on
    // unsupported bitrate values rather than clamping.
    const mimeCandidates = [
      "audio/webm;codecs=opus",
      "audio/webm",
      "audio/mp4",
    ];
    const mime = mimeCandidates.find((m) => MediaRecorder.isTypeSupported(m)) || "";
    try {
      const recorderOpts = mime ? { mimeType: mime, audioBitsPerSecond: 256000 } : { audioBitsPerSecond: 256000 };
      this.recorder = new MediaRecorder(this.recordingStream, recorderOpts);
    } catch (e) {
      console.warn("[engine] MediaRecorder 256kbps failed, falling back to default bitrate:", e);
      this.recorder = new MediaRecorder(this.recordingStream, mime ? { mimeType: mime } : undefined);
    }
    this.recordingTargetChannel = targetChannel;
    this.recordingModeUsed = mode;
    this.recorder.ondataavailable = (e) => {
      if (e.data && e.data.size > 0) this.recordingChunks.push(e.data);
    };
    this.recorder.start(250);
  }

  async stopRecording() {
    if (!this.recorder) return null;
    const stopped = new Promise((resolve) => {
      // iter-203 P0 — safety net so a wedged MediaRecorder can NEVER hang
      // the UI. Same guard as `stopTrackingSession`. Pre-iter-203 the user
      // reported "Stop button does nothing"; if `onstop` failed to fire,
      // this Promise never settled and the UI froze. Force-resolve after 8 s.
      let settled = false;
      const safeResolve = (v) => {
        if (settled) return;
        settled = true;
        resolve(v);
      };
      const timeoutId = setTimeout(() => {
        safeResolve({
          blob: null,
          buffer: null,
          channel: this.recordingTargetChannel,
          error: new Error("Stop timed out — the browser's MediaRecorder never fired its stop event. The take may still be partially saved; try recording again."),
        });
      }, 8000);
      this.recorder.onstop = async () => {
        clearTimeout(timeoutId);
        const blob = new Blob(this.recordingChunks, { type: this.recorder.mimeType || "audio/webm" });
        // Diagnostic: empty blob means the mic never delivered audio (permission
        // race, no input, or stop-too-fast). Surface a clear error so the
        // toast can guide the user to a fix.
        if (blob.size === 0) {
          safeResolve({
            blob,
            buffer: null,
            channel: this.recordingTargetChannel,
            error: new Error("No audio captured. The browser never delivered any sound from the mic. Common causes: mic was muted in the OS, browser permission was just denied, or you clicked Stop within a millisecond of Start."),
          });
          return;
        }
        const ab = await blob.arrayBuffer();
        try {
          const buf = await this.ctx.decodeAudioData(ab.slice(0));
          // iter-150 — peak-amplitude diagnostic. If the decoded buffer is
          // dead silent (peak < -80 dBFS = 1e-4) the user definitely won't
          // hear anything on playback. Surface a clear error so they know
          // it's a mic/permission issue, not a DAW playback bug.
          let peak = 0;
          for (let c = 0; c < buf.numberOfChannels; c++) {
            const data = buf.getChannelData(c);
            for (let i = 0; i < data.length; i++) {
              const v = Math.abs(data[i]);
              if (v > peak) peak = v;
            }
          }
          logger.info("[engine] recording captured:", {
            durationSec: buf.duration.toFixed(2),
            channels: buf.numberOfChannels,
            sampleRate: buf.sampleRate,
            peakDbfs: peak > 0 ? (20 * Math.log10(peak)).toFixed(1) : "-Inf",
            blobBytes: blob.size,
          });
          if (peak < 1e-4) {
            safeResolve({
              blob,
              buffer: null,
              channel: this.recordingTargetChannel,
              error: new Error(
                "Recording was silent (peak " +
                (peak > 0 ? (20 * Math.log10(peak)).toFixed(1) : "-Inf") +
                " dBFS). Check your OS mic input level, that the right input device is selected, and that the mic isn't muted at the hardware level."
              ),
            });
            return;
          }
          // Auto-trim leading silence (opt-out via `engine.autoTrimSilence = false`).
          // Stash the untrimmed buffer for one-click undo via `engine.undoLastTrim(ch)`.
          let finalBuf = buf;
          let trimmedSec = 0;
          if (this.autoTrimSilence !== false) {
            const trimmed = this.trimLeadingSilence(buf);
            finalBuf = trimmed.buffer;
            trimmedSec = trimmed.trimmedSec;
            if (trimmedSec > 0) {
              this._stashUndo(this.recordingTargetChannel, buf, `Recording-${Date.now()}.webm`);
            }
          }
          // Print mode — render the (possibly trimmed) dry buffer through the
          // channel's current FX chain. Stash the dry version for undo so the
          // user can revert one-click if they don't like the printed take.
          let printed = false;
          if (this.recordingModeUsed === "print") {
            const wet = await this._renderBufferThroughChannelFx(this.recordingTargetChannel, finalBuf);
            if (wet && wet !== finalBuf) {
              // Stash the (silence-trimmed) dry buffer. Overrides any silence-
              // trim stash — print is the "outer" destructive op so users undo
              // the whole thing in one click rather than peeling layers.
              this._stashUndo(this.recordingTargetChannel, finalBuf, `Recording-${Date.now()}.webm`);
              finalBuf = wet;
              printed = true;
            }
          }
          this.loadBufferToChannel(this.recordingTargetChannel, finalBuf, `Recording-${Date.now()}.webm`);
          safeResolve({ blob, buffer: finalBuf, channel: this.recordingTargetChannel, trimmedSec, printed, mode: this.recordingModeUsed });
        } catch (e) {
          safeResolve({ blob, buffer: null, channel: this.recordingTargetChannel, error: e });
        }
      };
    });
    // iter-203 — guard against double-stop on inactive recorders.
    try {
      if (this.recorder.state === "recording" || this.recorder.state === "paused") {
        this.recorder.stop();
      }
    } catch (e) {
      logger.warn("[engine] recorder.stop() threw:", e);
    }
    try { this.recordingStream?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    // Disconnect the live monitor BEFORE the await so the user stops hearing
    // their own mic the instant they hit Stop (avoid awkward extra-room-tone
    // tail through the FX chain).
    if (this.recordingMonitorSrc) {
      try { this.recordingMonitorSrc.disconnect(); } catch { /* ignore */ }
      this.recordingMonitorSrc = null;
    }
    const result = await stopped;
    this.recorder = null;
    this.recordingStream = null;
    this.recordingAnalyser = null;
    return result;
  }

  /* ─────────────────────────────  Multi-track tracking session  ─────────────────────────────
   * Workflow:
   *   1. User selects an audio input device (setInputDevice)
   *   2. User clicks "Arm" on each channel they want to record into (armChannel)
   *   3. User clicks Record (transport bar or per-channel rec) → startTrackingSession
   *      ONE getUserMedia call requests as many channels as needed; we use a
   *      ChannelSplitterNode to fan the multi-channel stream out to per-channel
   *      MediaStreamDestinationNodes, each wrapped in its own MediaRecorder.
   *   4. User clicks Stop → stopTrackingSession decodes every recorder into its
   *      armed DAW channel.
   * Note: Chrome typically clamps `channelCount` to 2 on most consumer interfaces.
   * Multi-channel interfaces (PreSonus StudioLive 64S, Focusrite Scarlett, etc.)
   * may expose more — we ask for ideal:maxChannel and gracefully accept whatever
   * Chrome gives us. Any channel whose source index exceeds the stream's actual
   * channel count falls back to channel 0 (mono mirror).
   */
  setInputDevice(deviceId) {
    this.inputDeviceId = deviceId || null;
  }

  /** Arm a DAW channel for the next tracking session.
   *  @param {number} channelIdx  0-63
   *  @param {number} sourceCh    which input-stream channel (0-based) feeds it. Defaults to 0. */
  armChannel(channelIdx, sourceCh = 0) {
    if (channelIdx < 0 || channelIdx >= this.channels.length) return;
    this.armedChannels.set(channelIdx, Math.max(0, Math.floor(sourceCh)));
  }
  disarmChannel(channelIdx) {
    this.armedChannels.delete(channelIdx);
  }
  clearArmed() {
    this.armedChannels.clear();
  }
  armedList() {
    return Array.from(this.armedChannels.entries()).map(([ch, src]) => ({ ch, src }));
  }
  isArmed(channelIdx) {
    return this.armedChannels.has(channelIdx);
  }

  /** Start a multi-channel tracking session for every currently-armed channel.
   *  @param {Object} opts
   *  @param {string} [opts.deviceId]      - audio input device
   *  @param {Object} [opts.streamToDisk]  - if provided, every MediaRecorder
   *                                         chunk is POSTed to the backend so
   *                                         long sessions don't fill browser
   *                                         memory. Shape: { backendUrl, sessionId, timesliceMs }
   *  Returns { ok, channelCountRequested, channelCountActual, channels:[{ch,src}], streamingToDisk }
   *  or throws the original mic error so the UI layer can map it via mapMicError. */
  /** Live peak (0..1) for the channel currently being recorded into. Returns 0
   *  when the channel isn't armed/recording. Used by the timeline lane / mixer
   *  row to draw a real-time level meter while tracking. */
  getRecordingPeak(ch) {
    let analyserNode = null;
    if (this.recordingAnalyser && this.recordingAnalyser.ch === ch) {
      analyserNode = this.recordingAnalyser.node;
    } else if (this.trackingSession) {
      const rec = this.trackingSession.recorders.find((r) => r.ch === ch);
      analyserNode = rec ? rec.analyser : null;
    }
    if (!analyserNode) return 0;
    const buf = this._recPeakBuf || (this._recPeakBuf = new Uint8Array(analyserNode.fftSize));
    analyserNode.getByteTimeDomainData(buf);
    let max = 0;
    for (let i = 0; i < buf.length; i++) {
      const v = Math.abs(buf[i] - 128);
      if (v > max) max = v;
    }
    return max / 128;
  }

  /** Seconds since the recording into this channel started, or 0 if not recording. */
  getRecordingElapsed(ch) {
    if (this.recordingAnalyser && this.recordingAnalyser.ch === ch) {
      return Math.max(0, this.ctx.currentTime - this.recordingAnalyser.startedAt);
    }
    if (this.trackingSession) {
      const rec = this.trackingSession.recorders.find((r) => r.ch === ch);
      if (rec) return Math.max(0, (Date.now() - this.trackingSession.startedAt) / 1000);
    }
    return 0;
  }

  /** Detect the first sample whose absolute amplitude exceeds `threshold` and
   *  return a new AudioBuffer that starts `prerollSec` seconds before that hit.
   *  Returns `{ buffer, trimmedSec }` so the caller can show a one-click "Undo"
   *  toast. If no leading silence is detected (first audible sample within
   *  `minSilenceSec`), returns the original buffer untouched + `trimmedSec=0`.
   *
   *  Audio is examined sample-by-sample across all channels — even a quiet
   *  cymbal hit on the L channel alone will keep its preceding silence.
   *  `maxSilenceSec` is a safety cap (default 12 s) — we never delete more
   *  than that even on an entirely silent take. */
  trimLeadingSilence(buffer, opts = {}) {
    const {
      threshold = 0.02,    // ~ -34 dBFS
      prerollSec = 0.05,   // keep 50 ms of room tone before the hit
      minSilenceSec = 0.5, // skip trim if hit lands inside the first 500 ms
      maxSilenceSec = 12,
    } = opts;
    if (!buffer || buffer.length === 0) return { buffer, trimmedSec: 0 };

    const sr = buffer.sampleRate;
    const minSamples = Math.floor(minSilenceSec * sr);
    const maxSamples = Math.floor(maxSilenceSec * sr);
    const prerollSamples = Math.floor(prerollSec * sr);

    let firstHit = -1;
    const channels = [];
    for (let c = 0; c < buffer.numberOfChannels; c++) channels.push(buffer.getChannelData(c));
    for (let i = 0; i < buffer.length; i++) {
      let max = 0;
      for (let c = 0; c < channels.length; c++) {
        const v = Math.abs(channels[c][i]);
        if (v > max) max = v;
      }
      if (max >= threshold) { firstHit = i; break; }
    }
    if (firstHit < 0 || firstHit < minSamples) return { buffer, trimmedSec: 0 };
    const cap = Math.min(firstHit, maxSamples);
    const trimStart = Math.max(0, cap - prerollSamples);
    if (trimStart === 0) return { buffer, trimmedSec: 0 };

    const newLen = buffer.length - trimStart;
    const out = this.ctx.createBuffer(buffer.numberOfChannels, newLen, sr);
    for (let c = 0; c < buffer.numberOfChannels; c++) {
      const src = buffer.getChannelData(c);
      const dst = out.getChannelData(c);
      dst.set(src.subarray(trimStart));
    }
    return { buffer: out, trimmedSec: trimStart / sr };
  }

  /** Undo cache — keyed by channel index, holds the buffer that was replaced
   *  by `loadBufferToChannel` so the user can revert an auto-trim with one click. */
  _undoCache = new Map();

  /** Store the channel's current buffer so it can be restored later. Called
   *  internally by the post-recording trim path before swapping in the trimmed
   *  buffer. Kept simple: only the *last* undo per channel is retained — older
   *  takes are GC'd when overwritten. */
  _stashUndo(ch, originalBuffer, originalName) {
    this._undoCache.set(ch, { buffer: originalBuffer, name: originalName });
  }

  /** Restore the previously-stashed buffer for a channel. Returns true on
   *  success, false if there's nothing to undo. */
  undoLastTrim(ch) {
    const cached = this._undoCache.get(ch);
    if (!cached) return false;
    this._undoCache.delete(ch);
    this.loadBufferToChannel(ch, cached.buffer, cached.name);
    return true;
  }

  /** iter-225 — Swap the currently-loaded buffer with whatever's in
   *  the undo cache for this channel, KEEPING the swap reversible.
   *  After the swap, the previously-loaded buffer is now in the cache
   *  and the previously-cached buffer is loaded — so calling
   *  `swapAB(ch)` again flips back. Used by Vocal Cleanup's A/B
   *  compare to toggle dry↔cleaned playback in place.
   *  Returns true on success, false when nothing is cached. */
  swapAB(ch) {
    const cached = this._undoCache.get(ch);
    if (!cached) return false;
    const channel = this.channels[ch];
    if (!channel || !channel.buffer) return false;
    const prevBuf = channel.buffer;
    const prevName = channel.name;
    const prevSourceUrl = channel.sourceUrl;
    // Load the cached buffer into the channel (this overwrites the
    // channel's buffer/name/sourceUrl) then stash the previous one so
    // the next call flips us back.
    this.loadBufferToChannel(ch, cached.buffer, cached.name, cached.sourceUrl || null);
    this._undoCache.set(ch, { buffer: prevBuf, name: prevName, sourceUrl: prevSourceUrl });
    return true;
  }

  async startTrackingSession({ deviceId = null, streamToDisk = null, nameResolver = null } = {}) {
    if (this.trackingSession) return { ok: false, error: "already-tracking" };
    if (this.armedChannels.size === 0) return { ok: false, error: "none-armed" };
    if (this.ctx.state === "suspended") await this.ctx.resume();

    // How many input channels we need = max source index + 1
    const armed = this.armedList();
    const maxSourceCh = Math.max(0, ...armed.map((a) => a.src));
    const channelCountRequested = maxSourceCh + 1;

    const effectiveDeviceId = deviceId || this.inputDeviceId || null;
    const constraints = {
      audio: {
        deviceId: effectiveDeviceId ? { exact: effectiveDeviceId } : undefined,
        // Wide constraint — Chrome will downsample if the device doesn't support it.
        channelCount: { ideal: channelCountRequested, min: 1 },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
    };
    const stream = await navigator.mediaDevices.getUserMedia(constraints);
    const track = stream.getAudioTracks()[0];
    const settings = track.getSettings();
    const channelCountActual = Number(settings.channelCount) || 1;

    // Build the audio graph: source → splitter → N destinations
    const sourceNode = this.ctx.createMediaStreamSource(stream);
    const splitter = this.ctx.createChannelSplitter(Math.max(channelCountActual, 1));
    sourceNode.connect(splitter);

    const recorders = [];
    // iter-203 P0 FIX — Audio bleed. Pre-iter-203 bug: when multiple armed
    // channels resolved to the SAME `actualSrc` (e.g. a mono mic with 2+
    // channels armed, all falling back to src=0), each armed channel got
    // its own recorder tapped off the same splitter output — so every
    // channel ended up with an IDENTICAL copy of the same audio. The user
    // perceived this as "audio bleeding into the next armed channel".
    //
    // Fix: deduplicate armed entries by `actualSrc`. Only the lowest-indexed
    // armed channel per unique input source gets a recorder. Skipped channels
    // are reported back to the UI via `skippedDups` so the user knows WHICH
    // channels won't record and WHY.
    const seenSrc = new Map();   // actualSrc → first armed ch that claimed it
    const validArmed = [];
    const skippedDups = [];
    for (const { ch, src } of armed) {
      const actualSrc = src < channelCountActual ? src : 0;
      if (seenSrc.has(actualSrc)) {
        skippedDups.push({ ch, dupOf: seenSrc.get(actualSrc), src, actualSrc });
        continue;
      }
      seenSrc.set(actualSrc, ch);
      validArmed.push({ ch, src, actualSrc });
    }
    const mime = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
      ? "audio/webm;codecs=opus"
      : MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "";

    // When stream-to-disk is on, we DON'T accumulate chunks locally — we POST
    // each one straight to the backend and forget the Blob (browser GC reclaims).
    // This keeps memory usage constant regardless of session length.
    const streaming = streamToDisk && streamToDisk.backendUrl && streamToDisk.sessionId
      ? { ...streamToDisk, timesliceMs: streamToDisk.timesliceMs || 2000 }
      : null;

    for (const { ch, src, actualSrc } of validArmed) {
      const dest = this.ctx.createMediaStreamDestination();
      try {
        splitter.connect(dest, actualSrc, 0);
      } catch (e) {
        splitter.connect(dest);
      }
      // Parallel analyser tap — same splitter output, separate node, no audio
      // path interference. Powers the live peak meter + scrolling waveform
      // overlay in the timeline lane / mixer row. Crucially, this analyser
      // ONLY sees the chosen splitter channel — internal DAW playback never
      // reaches it because the engine's playback graph (channel.gainNode →
      // masterDest) is completely disconnected from the recording graph.
      const analyser = this.ctx.createAnalyser();
      analyser.fftSize = 1024;
      analyser.smoothingTimeConstant = 0.4;
      try {
        splitter.connect(analyser, actualSrc, 0);
      } catch (e) {
        splitter.connect(analyser);
      }
      const recorder = new MediaRecorder(dest.stream, mime ? { mimeType: mime } : {});
      // Live monitor — same routing as single-track startRecording, but per
      // armed channel. Tap the SAME splitter output (so each desk input flows
      // through its own armed channel's FX chain) and feed into the channel's
      // fxChain.input. No effect on the recorder graph — purely additional.
      const mode = this.recordingMode || "dry";
      let monitorNode = null;
      if ((mode === "monitor" || mode === "print") && this.channels[ch]) {
        try {
          // Create a gain node as the monitor tap so we can splitter→gain→fxIn
          // (splitter outputs can be connected to multiple destinations).
          const monGain = this.ctx.createGain();
          try { splitter.connect(monGain, actualSrc, 0); } catch { splitter.connect(monGain); }
          monGain.connect(this.channels[ch].fxChain.input);
          monitorNode = monGain;
        } catch (e) {
          console.warn("[engine] multi-track monitor attach failed for ch", ch, e);
        }
      }
      const chunks = [];
      let seq = 0;
      let totalBytes = 0;
      const inflight = [];

      recorder.ondataavailable = (ev) => {
        if (!ev.data || ev.data.size === 0) return;
        if (streaming) {
          const thisSeq = seq++;
          totalBytes += ev.data.size;
          const form = new FormData();
          form.append("data", ev.data, `ch${String(ch).padStart(2, "0")}-${thisSeq}.webm`);
          form.append("seq", String(thisSeq));
          const url = `${streaming.backendUrl}/api/recording/chunk/${streaming.sessionId}/${ch}`;
          const p = fetch(url, { method: "POST", body: form, credentials: "include" })
            .then((r) => r.ok)
            .catch(() => false);
          inflight.push(p);
        } else {
          chunks.push(ev.data);
          totalBytes += ev.data.size;
        }
      };

      if (streaming) {
        // Timeslice triggers ondataavailable every N ms — required for live upload.
        recorder.start(streaming.timesliceMs);
      } else {
        recorder.start();
      }
      recorders.push({ ch, src, actualSrc, recorder, chunks, dest, analyser, monitorNode, totalBytesRef: () => totalBytes, inflight });
    }

    this.trackingSession = {
      stream, sourceNode, splitter, recorders,
      startedAt: Date.now(),
      streaming,
      mode: this.recordingMode || "dry",
      skippedDups,
      // iter-223 — Optional `(ch, actualSrc) => string` resolver supplied by
      // the React layer so the channel name can be derived from a user-set
      // mic alias (e.g. Mic 7 → "SM7B Take 1"). When null, the default
      // `Track-CHnn-inN.webm` naming is used.
      nameResolver,
    };
    return {
      ok: true,
      channelCountRequested,
      channelCountActual,
      channels: validArmed.map((v) => ({ ch: v.ch, src: v.src })),
      streamingToDisk: !!streaming,
      skippedDups,
    };
  }

  /** Stop the tracking session — finalises every per-channel MediaRecorder. In
   *  in-memory mode, decodes blobs and loads them into the matching channels
   *  immediately. In stream-to-disk mode, waits for in-flight uploads, calls
   *  the backend's /finalize endpoint, fetches the assembled webm files, and
   *  decodes those into the channels so the user can play them back. */
  async stopTrackingSession() {
    if (!this.trackingSession) return { ok: false, error: "not-tracking" };
    const { stream, sourceNode, splitter, recorders, streaming, skippedDups } = this.trackingSession;

    const results = await Promise.all(
      recorders.map(({ ch, src, actualSrc, recorder, chunks, inflight, totalBytesRef }) => new Promise((resolve) => {
        // iter-203 P0 — safety net so a wedged MediaRecorder can NEVER hang
        // the UI. Pre-iter-203 the user reported "Stop button does nothing":
        // if any recorder's `onstop` event failed to fire (browser bug or
        // weird state), this Promise.all blocked forever. Cap each recorder
        // at 8 s — after that, force-resolve with whatever chunks we have.
        let settled = false;
        const safeResolve = (v) => {
          if (settled) return;
          settled = true;
          resolve(v);
        };
        const timeoutId = setTimeout(() => {
          safeResolve({ ch, src, actualSrc, ok: false, error: "stop-timeout (recorder never fired onstop)" });
        }, 8000);
        recorder.onstop = async () => {
          clearTimeout(timeoutId);
          try {
            if (streaming) {
              // Wait for in-flight chunk uploads before finalising.
              await Promise.all(inflight);
              safeResolve({ ch, src, actualSrc, ok: true, bytes: totalBytesRef ? totalBytesRef() : 0, streamed: true });
              return;
            }
            const blob = new Blob(chunks, { type: recorder.mimeType || "audio/webm" });
            const ab = await blob.arrayBuffer();
            const buf = await this.ctx.decodeAudioData(ab.slice(0));
            // iter-150 — peak-amplitude diagnostic per armed channel. Helps
            // the user see WHICH input lane was silent on multi-track.
            try {
              let peak = 0;
              for (let c = 0; c < buf.numberOfChannels; c++) {
                const data = buf.getChannelData(c);
                for (let i = 0; i < data.length; i++) {
                  const v = Math.abs(data[i]);
                  if (v > peak) peak = v;
                }
              }
              logger.info(`[engine] multi-track ch${ch} captured:`, {
                durationSec: buf.duration.toFixed(2),
                channels: buf.numberOfChannels,
                sampleRate: buf.sampleRate,
                peakDbfs: peak > 0 ? (20 * Math.log10(peak)).toFixed(1) : "-Inf",
                blobBytes: blob.size,
                inputSrc: src,
              });
            } catch { /* diagnostic only */ }
            const name = (this.trackingSession?.nameResolver?.(ch, actualSrc))
              || `Track-CH${String(ch + 1).padStart(2, "0")}-in${actualSrc}.webm`;
            let finalBuf = buf;
            let trimmedSec = 0;
            if (this.autoTrimSilence !== false) {
              const trimmed = this.trimLeadingSilence(buf);
              finalBuf = trimmed.buffer;
              trimmedSec = trimmed.trimmedSec;
              if (trimmedSec > 0) this._stashUndo(ch, buf, name);
            }
            // Print mode — render the dry buffer through this channel's FX
            // before loading. Stash dry for undo (overrides silence-trim stash).
            let printed = false;
            if (this.trackingSession?.mode === "print") {
              const wet = await this._renderBufferThroughChannelFx(ch, finalBuf);
              if (wet && wet !== finalBuf) {
                this._stashUndo(ch, finalBuf, name);
                finalBuf = wet;
                printed = true;
              }
            }
            this.loadBufferToChannel(ch, finalBuf, name);
            safeResolve({ ch, src, actualSrc, ok: true, duration: finalBuf.duration, streamed: false, trimmedSec, printed });
          } catch (e) {
            safeResolve({ ch, src, actualSrc, ok: false, error: e.message });
          }
        };
        try {
          // iter-203 — guard against double-stop (recorder.stop() on an
          // already-inactive recorder throws InvalidStateError).
          if (recorder.state === "recording" || recorder.state === "paused") {
            recorder.stop();
          } else {
            clearTimeout(timeoutId);
            safeResolve({ ch, src, actualSrc, ok: false, error: `recorder state ${recorder.state}` });
          }
        } catch (e) {
          clearTimeout(timeoutId);
          safeResolve({ ch, src, actualSrc, ok: false, error: e.message });
        }
      })),
    );
    stream.getTracks().forEach((t) => t.stop());
    // Disconnect every per-recorder live-monitor tap so the user stops hearing
    // their own wet mic the instant they hit Stop.
    for (const r of recorders) {
      if (r.monitorNode) {
        try { r.monitorNode.disconnect(); } catch { /* ignore */ }
        r.monitorNode = null;
      }
      // iter-203 — fully disconnect each recorder's analyser + destination
      // from the splitter so the audio graph is GC-able and a subsequent
      // tracking session starts clean. Leaving these nodes connected
      // contributed to perceived "bleed" symptoms across back-to-back takes.
      try { r.analyser?.disconnect(); } catch { /* ignore */ }
      try { r.dest?.disconnect(); } catch { /* ignore */ }
    }
    // iter-203 — disconnect the source + splitter so a future tracking
    // session builds a fresh graph rather than appending to a stale chain.
    try { splitter?.disconnect(); } catch { /* ignore */ }
    try { sourceNode?.disconnect(); } catch { /* ignore */ }

    // Stream-to-disk: finalise on backend, fetch each file URL, decode, load.
    let finalize = null;
    if (streaming) {
      try {
        const r = await fetch(`${streaming.backendUrl}/api/recording/finalize/${streaming.sessionId}`, { method: "POST", credentials: "include" });
        finalize = r.ok ? await r.json() : { error: `finalize-${r.status}` };
        if (finalize && finalize.files) {
          // Decode each saved file back into its channel for instant playback.
          for (const { ch, actualSrc } of recorders) {
            const fileKey = `ch${String(ch).padStart(2, "0")}`;
            const path = finalize.files[fileKey];
            if (!path) continue;
            try {
              const aliased = this.trackingSession?.nameResolver?.(ch, actualSrc);
              const fname = aliased || `Track-CH${String(ch + 1).padStart(2, "0")}-in${actualSrc}.webm`;
              await this.loadUrlToChannel(ch, `${streaming.backendUrl}${path}`, fname);
            } catch (e) {
              // best-effort — surface in the result row
              const row = results.find((r2) => r2.ch === ch);
              if (row) row.loadError = e.message;
            }
          }
        }
      } catch (e) {
        finalize = { error: e.message };
      }
    }

    this.trackingSession = null;
    return { ok: true, results, streaming: !!streaming, finalize, skippedDups: skippedDups || [] };
  }

  isTracking() {
    return !!this.trackingSession;
  }

  /** Total bytes streamed across all per-channel recorders in the current session.
   *  Useful for live "X MB uploaded" status while tracking. */
  getStreamBytesTotal() {
    if (!this.trackingSession) return 0;
    return this.trackingSession.recorders.reduce(
      (sum, r) => sum + (r.totalBytesRef ? r.totalBytesRef() : 0),
      0,
    );
  }

  /** Render the current mix offline. If `masteringParams` is provided, applies a real
   *  mastering chain (EQ + comp + limiter + makeup) using those params during the render.
   *  Returns the rendered AudioBuffer. */
  async _renderMixOffline(masteringParams = null, only = null, ignoreMute = false) {
    const ownLen = only ? Math.max(0, ...only.map((i) => this.channels[i]?.buffer?.duration || 0)) : 0;
    const duration = Math.max(0.1, only ? ownLen : (this.maxDuration || 1));
    const sampleRate = this.ctx.sampleRate;
    const offline = new OfflineAudioContext(2, Math.ceil(duration * sampleRate), sampleRate);
    const masterGain = offline.createGain();
    masterGain.gain.value = this.masterGain.gain.value;
    const limiter = offline.createDynamicsCompressor();
    limiter.threshold.value = this.limiter.threshold.value;
    limiter.knee.value = this.limiter.knee.value;
    limiter.ratio.value = this.limiter.ratio.value;

    if (masteringParams) {
      const chain = buildMasteringChain(offline, masterGain, limiter);
      chain.apply(masteringParams);
    } else {
      masterGain.connect(limiter);
    }
    limiter.connect(offline.destination);

    const anySolo = only ? only.some((i) => this.channels[i]?.solo && this.channels[i]?.buffer) : this.anySolo();
    this.channels.forEach((c, i) => {
      if (!c.buffer) return;
      if (only && !only.includes(i)) return;
      const effective = (!ignoreMute && (c.muted || (anySolo && !c.solo))) ? 0 : c.volume / 100;
      if (effective === 0) return;

      // Channel volume + pan feed into master
      const vol = offline.createGain();
      vol.gain.value = effective;
      const pan = offline.createStereoPanner();
      pan.pan.value = c.pan / 50;
      vol.connect(pan);
      pan.connect(masterGain);

      // Per-channel FX chain feeds the volume node
      const fxChain = buildFxChain(offline, vol);
      EFFECT_KEYS.forEach((k) => fxChain.applyEffect(k, c.fx[k], c.fxParams[k]));
      if (!this.dryMode) {  // match live playback: Wet mode opens the reverb / delay returns
        fxChain.nodes.reverbWet.gain.value = 1;
        fxChain.nodes.delayWet.gain.value = 1;
      }

      const src = offline.createBufferSource();
      src.buffer = c.buffer;
      fxChain.connectPitchTo(src.detune);
      src.connect(fxChain.input);
      src.start(0);
    });

    return offline.startRendering();
  }

  /** Render the mix — optionally only some channels (`ignoreMute` renders them even when muted) — to an AudioBuffer. */
  renderMix({ only = null, ignoreMute = false, mastered = true } = {}) {
    return this._renderMixOffline(mastered ? (this.lastMastering?.recipe?.params || null) : null, only, ignoreMute);
  }

  // Render current mix to WAV (offline). Applies the last applied mastering recipe if any.
  // `opts.bitDepth` 24 → studio 24-bit PCM (Creator export); default 16-bit.
  async exportWav(opts = {}) {
    const params = this.lastMastering?.recipe?.params || null;
    const rendered = await this._renderMixOffline(params);
    if (opts.bitDepth === 24) return encodeWav(rendered, { bitDepth: 24 });
    return audioBufferToWavBlob(rendered);
  }

  /**
   * Real AI mastering — measure, recipe, apply, re-measure.
   * @param {string|{label:string,lufs:number,ceiling:number}} targetKey  a MASTERING_TARGETS key
   *   ("streaming"|"album"|"loud"|"podcast") or a custom target object (Creator "how will you use it").
   * @returns {Promise<{before, after, recipe, target}>}  full stats for the UI to display
   */
  async runMastering(targetKey = "streaming") {
    if (this.maxDuration <= 0) {
      throw new Error("Load or record audio before mastering.");
    }
    const target = typeof targetKey === "string" ? MASTERING_TARGETS[targetKey] : targetKey;
    if (!target || typeof target.lufs !== "number") {
      throw new Error(`Unknown mastering target: ${typeof targetKey === "string" ? targetKey : JSON.stringify(targetKey)}`);
    }

    const dry = await this._renderMixOffline(null);
    const [lufsBefore, tiltBefore] = await Promise.all([
      measureLufs(dry),
      measureSpectralTilt(dry),
    ]);
    const peakBefore = measureTruePeak(dry);

    const recipe = suggestRecipe({
      currentLufs: lufsBefore,
      currentTruePeakDb: peakBefore,
      tilt: tiltBefore,
      target,
    });

    // Apply to live chain so playback hears it
    this.masteringChain.apply(recipe.params);

    // Render again WITH the chain to measure result
    const wet = await this._renderMixOffline(recipe.params);
    const [lufsAfter, tiltAfter] = await Promise.all([
      measureLufs(wet),
      measureSpectralTilt(wet),
    ]);
    const peakAfter = measureTruePeak(wet);

    const stats = {
      before: { lufs: lufsBefore, truePeakDb: peakBefore, tilt: tiltBefore },
      after:  { lufs: lufsAfter,  truePeakDb: peakAfter,  tilt: tiltAfter  },
      recipe,
      target: recipe.target,
    };
    this.lastMastering = stats;
    return stats;
  }

  /** Remove the mastering chain (set to identity). */
  resetMastering() {
    this.masteringChain.reset();
    this.lastMastering = null;
  }

  /** Encode a single channel's raw buffer to a WAV Blob (no FX, no volume) — for shipping
   *  unmodified source audio to backend services like stem extraction. */
  channelToWavBlob(idx) {
    const ch = this.channels[idx];
    if (!ch || !ch.buffer) return null;
    return audioBufferToWavBlob(ch.buffer);
  }

  /** Fetch an audio URL, decode it, and load into a target channel.
   * iter-214 — Remembers the original URL on the channel so stem
   * extraction can call /api/stems/extract_url (skipping the slow
   * upload step) instead of bouncing the channel to a WAV blob first.
   * Pass `sourceUrl` explicitly when the public URL differs from the
   * fetched URL (e.g. proxied playable URL vs raw Suno URL). */
  async loadUrlToChannel(idx, url, name, sourceUrl = null) {
    if (this.ctx.state === "suspended") await this.ctx.resume();
    const api = process.env.REACT_APP_BACKEND_URL;
    const own = url.startsWith("/api/") || (api && url.startsWith(`${api}/api/`));
    const res = await fetch(url, own ? { credentials: "include" } : undefined);
    if (!res.ok) throw new Error(`Failed to fetch ${url}: ${res.status}`);
    const ab = await res.arrayBuffer();
    const buf = await this.ctx.decodeAudioData(ab);
    this.loadBufferToChannel(idx, buf, name || url.split("/").pop(), sourceUrl || url);
    return buf;
  }

  /** Find the lowest-index channels that have no buffer; up to `n`. */
  freeChannelIndices(n) {
    const out = [];
    for (let i = 0; i < this.channels.length && out.length < n; i++) {
      if (!this.channels[i].buffer) out.push(i);
    }
    return out;
  }

  /** Empty a channel (stop it, drop its audio) and shrink the song length to what's left. */
  clearChannel(idx) {
    const ch = this.channels[idx];
    if (!ch) return;
    ch.stop();
    Object.assign(ch, { buffer: null, name: null, duration: 0, waveform: null, sourceUrl: null });
    this.maxDuration = this.channels.reduce((m, c) => Math.max(m, c.buffer?.duration || 0), 0);
  }
}
